import React, { useEffect, useMemo, useState } from 'react';
import { FaKey } from 'react-icons/fa';
import KeyBadge, { KeyRoleIcon } from '../create/keys/KeyBadge';
import { TUTORIAL_TARGETS, tutorialAttr } from '../../../tutorials';
import styles from './styles/PreviewKeyTray.module.css';

const NOTICE_MS = 2500;

/** A held key may be a bare id or a populated document. */
const toKeyId = (entry) => {
    if (entry && typeof entry === 'object') return String(entry._id ?? entry.id ?? '');
    return String(entry);
};

/**
 * While an author previews their draft, shows which keys they are holding and
 * announces each one as it is picked up or taken away — otherwise the only sign
 * a key did anything is a choice quietly appearing or disappearing.
 *
 * Announcements come from `lastChange`, which only play produces, so clearing
 * keys on restart stays quiet.
 *
 * Preview only: players never see this.
 *
 * @param {{
 *   keys: {id: string, name: string}[],   the draft's keys
 *   userKeys: Array<string|object>,        every key held
 *   lastChange?: {id: number, gained: string[], lost: string[]}|null,
 * }} props
 */
const PreviewKeyTray = ({ keys = [], userKeys = [], lastChange = null }) => {
    const byId = useMemo(
        () => new Map(keys.map((key) => [String(key.id), key])),
        [keys]
    );

    // Only this draft's keys count; a key deleted since it was picked up is gone.
    const held = useMemo(() => {
        const seen = new Set();
        return userKeys
            .map(toKeyId)
            .filter((id) => byId.has(id) && !seen.has(id) && seen.add(id));
    }, [userKeys, byId]);

    const [notices, setNotices] = useState([]);

    useEffect(() => {
        if (!lastChange) return undefined;
        const names = (ids) =>
            (ids || [])
                .map(toKeyId)
                .filter((id) => byId.has(id))
                .map((id) => byId.get(id).name);

        const next = [];
        const gained = names(lastChange.gained);
        const lost = names(lastChange.lost);
        if (gained.length > 0) next.push({ role: 'grant', text: `Picked up: ${gained.join(', ')}` });
        if (lost.length > 0) next.push({ role: 'remove', text: `Taken away: ${lost.join(', ')}` });
        if (next.length === 0) return undefined;

        setNotices(next);
        const timer = setTimeout(() => setNotices([]), NOTICE_MS);
        return () => clearTimeout(timer);
        // Keyed on the change itself: renaming a key must not replay the notice.
    }, [lastChange?.id]);

    if (keys.length === 0) return null;

    return (
        <div className={styles.tray} {...tutorialAttr(TUTORIAL_TARGETS.PREVIEW_KEY_TRAY)}>
            {notices.map((notice) => (
                <div
                    key={notice.role}
                    className={`${styles.notice} ${notice.role === 'remove' ? styles.noticeRemove : ''}`}
                    role="status"
                    aria-live="polite"
                >
                    {notice.role === 'remove' ? (
                        <KeyRoleIcon role="remove" size={12} color="#fff" />
                    ) : (
                        <FaKey size={12} />
                    )}
                    <span>{notice.text}</span>
                </div>
            ))}
            <div className={styles.strip}>
                <span className={styles.label}>Keys you hold</span>
                {held.length > 0 ? (
                    <div className={styles.badges}>
                        {held.map((id) => (
                            <KeyBadge key={id} role="grant" size="sm" name={byId.get(id).name} />
                        ))}
                    </div>
                ) : (
                    <span className={styles.empty}>No keys yet</span>
                )}
            </div>
        </div>
    );
};

export default PreviewKeyTray;
